import React, { ForwardedRef, PropsWithChildren, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MultiSelect } from '../ListView/ListViewRenderHelpers';
import { MultiAction } from '../ListViewActions';

export type MultiselectProviderProps = {
	active?: boolean;
	showToggleAction?: boolean;
};

const getCountAndSelectedIds = (selectedIdMap: { [id: number]: boolean }) => {
	return Object.keys(selectedIdMap)
		.map(Number)
		.reduce(
			(res, id) => {
				if (selectedIdMap[id]) {
					res.selected += 1;
					res.selectedIds.push(id);
				}
				return res;
			},
			{ selected: 0, selectedIds: [] as number[] }
		);
};

export type MultiSelectContext = {
	active: boolean;
	allSelected: boolean;
	selected: number;
	selectedIds: number[];
	selectedIdMap: Record<number, boolean>;
	deselectedIds: number[];
	deselectedIdMap: Record<number, boolean>;
	multiActions: MultiAction[];
	total: number;
	showToggleAction: boolean;
	setTotal: (total: number) => void;
	selectAllResult: () => Promise<void>;
	setSelectedIds: (selectedIds: MultiSelect['selectedIds']) => Promise<void>;
	toggleSelected: (id: number, checked?: boolean) => void;
	setMultiActions: (actions: MultiAction[]) => void;
	selectNone: () => void;
	reset: () => void;
	toggleActive: () => void;
};

const defaultFnUsedWarning = (fnName: string) => () => {
	console.error(`MultiselectProvider: ${fnName} was used outside of context`);
	return Promise.resolve();
};

const MultiselectContext = createContext<MultiSelectContext>({
	active: false,
	allSelected: false,
	selected: 0,
	selectedIds: [],
	multiActions: [],
	selectedIdMap: {},
	deselectedIds: [],
	deselectedIdMap: {},
	total: 0,
	showToggleAction: false,
	setTotal: defaultFnUsedWarning('setTotal'),
	selectAllResult: defaultFnUsedWarning('selectAllResult'),
	setSelectedIds: defaultFnUsedWarning('setSelectedIds'),
	toggleSelected: defaultFnUsedWarning('toggleSelected'),
	setMultiActions: defaultFnUsedWarning('setMultiActions'),
	selectNone: defaultFnUsedWarning('selectNone'),
	reset: defaultFnUsedWarning('reset'),
	toggleActive: defaultFnUsedWarning('toggleActive')
});

export const useMultiselect = () => useContext(MultiselectContext);

const useMultiselectData = (initialValue?: MultiselectProviderProps): MultiSelectContext => {
	const [selected, setSelected] = useState<MultiSelectContext['selected']>(0);
	const [total, setTotal] = useState<MultiSelectContext['selected']>(0);
	const [selectedIds, setSelectedIds] = useState<MultiSelectContext['selectedIds']>([]);
	const [selectedIdMap, setSelectedIdMap] = useState<MultiSelectContext['selectedIdMap']>({});
	const [deselectedIds, setDeselectedIds] = useState<MultiSelectContext['deselectedIds']>([]);
	const deselectedIdMap = useMemo(
		() => deselectedIds.reduce((res, id) => ({ ...res, [id]: true }), {} as Record<number, boolean>),
		[deselectedIds]
	);
	const [multiActions, setMultiActions] = useState<MultiSelectContext['multiActions']>([]);
	const [active, setActive] = useState<MultiSelectContext['active']>(initialValue?.active ?? true);
	const [selectAll, setSelectAll] = useState<MultiSelectContext['allSelected']>(false);
	const cbRef = useRef<(value?: any) => void>();

	const selectNone = () => {
		setSelected(0);
		setSelectedIds([]);
		setSelectedIdMap({});
		setDeselectedIds([]);
		setSelectAll(false);
	};

	useEffect(() => {
		cbRef.current?.();
		cbRef.current = undefined;
	}, [selected]);

	return {
		multiActions,
		active: active && !!multiActions.length,
		showToggleAction: initialValue?.showToggleAction ?? false,
		allSelected: selectAll,
		selected,
		selectedIds,
		selectedIdMap,
		deselectedIds,
		deselectedIdMap,
		total,
		setMultiActions,
		// setSelectedIdMap should never be exposed to the outside world. Setting setSelectedIds will generate the map
		setSelectedIds: ids => {
			return new Promise(resolve => {
				cbRef.current = resolve;
				const currentSelectedIds = Array.from(new Set([...selectedIds, ...ids]));
				setSelected(currentSelectedIds.length);
				setSelectedIds(currentSelectedIds);
				setSelectedIdMap(currentSelectedIds.reduce((res, id) => ({ ...res, [id]: true }), {}));
				setDeselectedIds([]);
				setSelectAll(false);
			});
		},
		setTotal: total => {
			if (selectAll) {
				setSelected(total - deselectedIds.length);
			}
			setTotal(total);
		},
		toggleSelected: (id, checked) => {
			if (selectAll) {
				// In "select all" mode, toggling means deselecting/reselecting individual rows
				const isCurrentlyDeselected = deselectedIdMap[id];
				if (checked === true || (checked === undefined && isCurrentlyDeselected)) {
					// Re-select: remove from deselected
					const newDeselectedIds = deselectedIds.filter(did => did !== id);
					setDeselectedIds(newDeselectedIds);
					setSelected(total - newDeselectedIds.length);
				} else {
					// Deselect: add to deselected
					const newDeselectedIds = [...deselectedIds, id];
					setDeselectedIds(newDeselectedIds);
					const newSelected = total - newDeselectedIds.length;
					if (newSelected <= 0) {
						// If all are deselected, exit select-all mode
						selectNone();
					} else {
						setSelected(newSelected);
					}
				}
				return;
			}

			const map = {
				...selectedIdMap,
				[id]: checked ?? !selectedIdMap[id] // if checked was not provided we toggle current value
			};
			const { selected, selectedIds } = getCountAndSelectedIds(map);

			setSelected(selected);
			setSelectedIds(selectedIds);
			setSelectedIdMap(map);
			setSelectAll(false);
		},
		selectAllResult: () => {
			return new Promise(resolve => {
				if (total === selected && deselectedIds.length === 0) {
					return resolve();
				}

				cbRef.current = resolve;
				setSelected(total);
				setSelectedIds([]);
				setSelectedIdMap({});
				setDeselectedIds([]);
				setSelectAll(true);
			});
		},
		selectNone,
		reset: () => {
			selectNone();
			setTotal(0);
			setSelectAll(false);
		},
		toggleActive: () => {
			if (active) selectNone();
			setActive(!active);
		}
	};
};

const MultiselectProvider = ({ children, ...initialValue }: PropsWithChildren<MultiselectProviderProps>) => {
	const data = useMultiselectData(initialValue);
	return <MultiselectContext.Provider value={data}>{children}</MultiselectContext.Provider>;
};

export const withMultiselect = <P extends {}>(
	Component: React.ComponentType<P>,
	initialValue?: MultiselectProviderProps
) => {
	return (props: P) => {
		const data = useMultiselectData(initialValue);
		return (
			<MultiselectContext.Provider value={data}>
				<Component {...props} multiselect={data} />
			</MultiselectContext.Provider>
		);
	};
};

export const withMultiselectForwardRef = <P extends {}>(
	Component: React.ComponentType<P>,
	initialValue?: MultiselectProviderProps
) => {
	const WithMultiselect = withMultiselect<P & { forwardedRef: ForwardedRef<unknown> }>(
		({ forwardedRef, ...props }) => (
			// eslint-disable-next-line react/jsx-key
			<Component ref={forwardedRef} {...(props as P)} />
		),
		initialValue
	);

	return React.forwardRef<unknown, P>((props, ref) => <WithMultiselect {...props} forwardedRef={ref} />);
};

export default MultiselectProvider;
